import BigNumber from 'bignumber.js'
import { BetDetails } from 'config/types/profile'
import { usePublicSocket } from 'hooks/usePublicSocket'
import { useRequest } from 'hooks/useRequest'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { parseBetDetailsItemMapper } from 'services/mapper/Game'
import { BaseResponse, HunnyRequest, Paging } from 'services/types'
import { useUserInfo } from 'state/auth/hooks'

type BetState = {
  animationRef: {
    disabled?: boolean
  }
  active?: boolean
  item: BetDetails
}

const useDisplayLiveBet = (
  validateBet: (bet: BetDetails) => boolean,
  config: { animationDuration: number; limit: number },
  initBetsFn?: () => HunnyRequest<BaseResponse<Paging<BetDetails>>>,
) => {
  const { execute } = useRequest()
  const [bets, setBets] = useState<BetState[]>([])
  const socket = usePublicSocket()
  const { uid: currentPlayerId } = useUserInfo()
  const [isFetching, setIsFetching] = useState(false)

  const liveBetDataRef = useRef<{
    isAnimating: boolean
    isPause?: boolean
    pendingBet: BetState[]
  }>({
    isAnimating: false,
    isPause: false,
    pendingBet: [],
  })
  const queueRef = useRef<BetDetails[]>([])

  const fetchHistory = useCallback(async (): Promise<Paging<BetDetails>> => {
    if (!initBetsFn) return
    setIsFetching(true)
    const response = await execute(initBetsFn())

    if (!response?.data) {
      setIsFetching(false)
      setBets([])
      return
    }

    const rowData: BetDetails[] = response.data?.items

    setBets(
      rowData.map((item, index) => ({
        animationRef: {
          disabled: true,
        },
        active: index % 2 !== 0,
        item,
      })),
    )
    setIsFetching(false)
  }, [initBetsFn])

  useEffect(() => {
    fetchHistory()
  }, [fetchHistory])

  const processQueue = useCallback(() => {
    if (liveBetDataRef.current.isAnimating || queueRef.current.length === 0) {
      return
    }
    const newBet = queueRef.current.shift()

    liveBetDataRef.current.isAnimating = true

    const animationRef = {
      disabled: liveBetDataRef.current.isPause,
    }

    const bet: BetState = {
      animationRef,
      item: newBet,
    }

    const animatedDuration = !liveBetDataRef.current.isPause ? config.animationDuration + 100 : 10

    if (liveBetDataRef.current.isPause) {
      liveBetDataRef.current.pendingBet = [bet, ...liveBetDataRef.current.pendingBet.slice(0, config.limit - 1)]
    } else {
      setBets((preState) => {
        const isBetActive =
          (liveBetDataRef.current.pendingBet.length + 1) % 2 === 0 ? preState[0]?.active : !preState[0]?.active

        const newBets = [bet, ...liveBetDataRef.current.pendingBet].map((item, index) => {
          item.active = index % 2 === 0 ? isBetActive : !isBetActive
          return item
        })
        liveBetDataRef.current.pendingBet = []
        return [...newBets, ...preState].slice(0, config.limit)
      })
    }

    setTimeout(() => {
      liveBetDataRef.current.isAnimating = false
      animationRef.disabled = true
      processQueue()
    }, animatedDuration + 100)
  }, [])

  useEffect(() => {
    if (!socket) {
      return
    }

    const event = socket.on('social.live.bet', (newBet) => {
      if (newBet) {
        const parsedNewBet = parseBetDetailsItemMapper(newBet)

        if (!parsedNewBet.betId || !validateBet(parsedNewBet)) {
          return
        }

        if (queueRef.current.length > config.limit * 0.3) {
          const betAmountInUsd = new BigNumber(parsedNewBet.bet?.amountInUsd || 0)
          const payoutInUsd = new BigNumber(parsedNewBet.payout?.amountInUsd || 0)
          const multiplier = parsedNewBet.multiplier || 0
          if (
            payoutInUsd.lt(1) &&
            betAmountInUsd.lt(1) &&
            multiplier < 2 &&
            parsedNewBet.player.uid !== currentPlayerId
          ) {
            return
          }
        }

        if (queueRef.current.length > config.limit) {
          queueRef.current.shift()
        }

        if (parsedNewBet.player.uid === currentPlayerId) {
          queueRef.current.unshift(parsedNewBet)
        } else {
          queueRef.current.push(parsedNewBet)
        }

        processQueue()
      }
    })

    return () => {
      event.off()
      queueRef.current = []
    }
  }, [socket, validateBet, currentPlayerId])

  const pause = useCallback(() => {
    liveBetDataRef.current.isPause = true
  }, [])

  const play = useCallback(() => {
    liveBetDataRef.current.isPause = false
  }, [])

  return useMemo(
    () => ({
      pause,
      play,
      bets,
      isFetching,
    }),
    [bets, isFetching],
  )
}

export default useDisplayLiveBet
